import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {FITHGameConfig} from "../FITHGameConfig";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineLite, TimelineMax, TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {FITHParsedGameData} from "../server/data/FITHParsedGameData";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {FITHParsedInitData} from "../server/data/FITHParsedInitData";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {FITHSoundManager} from "../sound/FITHSoundManager";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";
import {FireInTheHole} from "../FireInTheHole";

export class FITHReelFrameView extends BaseView {
    private _barrier: PIXI.Sprite;
    private _barrierMask: PIXI.Graphics;
    private _reel: PIXI.Sprite;
    private _trolley: PIXI.Sprite;
    private _fsThemeOutlineGlow: TimelineSprite[] = [];
    private _leftBoard: PIXI.Sprite;
    private _leftBoardRed: PIXI.Sprite;
    private _dotRed: PIXI.Sprite;
    private _dotRedTL: TimelineMax;
    private _rightBoard: PIXI.Sprite;
    private _fsRemaining: BitmapCountUp;
    private _fsTotalMultiplier: BitmapCountUp;
    private _fsRemainingValue: number;
    private _multiplierRestore: number = 0;
    private _leftBoardAnimation: TimelineSprite;
    private _rightBoardAnimation: TimelineSprite;
    private _winUpToTextItem: PIXI.Text;
    private _nextMode: string = "";
    private _activeRows: number;
    private _hasKilledDwarf: boolean = false;
    private _killedDwarf: PIXI.Sprite;

    private _isLastSpin: boolean = false;

    /**
     * @description Entering FS.
     */
    public enterFS(): void {
        this.toggleBarrierVisibility(false);

        this._reel.texture = GameResources.getTextures("FS_reel")[0];

        this.toggleSideBoards(true);

        this.updateTotalMultiplierBoardText(this._multiplierRestore);
        this.updateSpinsLeftBoardText();
        this._winUpToTextItem.visible = false;
        this._fsThemeOutlineGlow.forEach((outlineGlow: TimelineSprite) => {
            outlineGlow.playLoop();
            outlineGlow.show();
        });
    }

    /**
     * @description Update total multiplier(the right board) text.
     * @param multiplier
     */
    public updateTotalMultiplierBoardText(multiplier: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        // #335
        multiplier = Math.min(multiplier, 60000);

        // #205
        if (multiplier > 0 && ((multiplier + "") !== this._fsTotalMultiplier.text.split("x")[0])) {
            tl.add([
                () => {
                    this._fsTotalMultiplier.text = multiplier + "x";
                    while (this._fsTotalMultiplier.textWidth * this._fsTotalMultiplier.scaling > this._fsTotalMultiplier.maxTextWidth) {
                        this._fsTotalMultiplier.fontSize--;
                        this._fsTotalMultiplier.dirty = true;
                        this._fsTotalMultiplier.updateTransform();
                    }
                    this.setFSMultiplierPivot();

                    // #269
                    FITHSoundManager.playMultiplierUp();
                },
                this._rightBoardAnimation.getAnimationAutoShowHide(true, true)
            ], 0);
        }

        return tl;
    }

    /**
     * @description Update spins left(the left board) text.
     * @param value
     */
    public updateSpinsLeftBoardText(value?: number): void {
        const tl: TimelineLite = new TimelineLite(),
            duration: number = 5 / 30,
            fsRemaining: number = (value ? value : this._fsRemainingValue);

        // #205
        if (this._fsRemaining.currentValue) {
            this._fsRemaining.visible = true;
            this._fsRemaining.alpha = 1;
        }

        if (fsRemaining !== this._fsRemaining.currentValue && fsRemaining > -1) {
            this._leftBoardRed.visible = true;
            this._leftBoardRed.alpha = 0;

            tl.add([
                TweenLite.to(this._fsRemaining, duration, {alpha: 0}),
                TweenLite.to(this._fsRemaining.scale, duration, {x: 0.7, y: 0.7}),
                TweenLite.to(this._leftBoardRed, duration, {alpha: 1})
            ], 0);
            tl.add([
                () => {
                    this._fsRemaining.alpha = 1;
                    this._fsRemaining.getCountUpAnimation(fsRemaining, fsRemaining, {
                        duration: 0
                    });
                    TweenLite.to(this._fsRemaining.scale, duration, {x: 1, y: 1});
                },
                TweenLite.to(this._leftBoardRed, duration, {
                    alpha: 0,
                    onComplete: () => this._leftBoardRed.visible = false
                })
            ], duration);

            if (fsRemaining === 0) {
                tl.add(() => {
                    this._dotRed.visible = true;
                    this._dotRed.alpha = 0;
                    this._dotRedTL.restart();
                    this._isLastSpin = true;
                }, (duration * 3));
            } else if (this._isLastSpin) {
                tl.add([
                    this._leftBoardAnimation.getAnimationAutoShowHide(true, true),
                    () => {
                        this._isLastSpin = false;
                        this._dotRed.visible = false;
                        this._dotRedTL.pause();
                        this._dotRedTL.kill();
                    }
                ], 0);
            }
        }
    }

    /**
     * @description Exiting FS.
     */
    public exitFS(): void {
        this.toggleBarrierVisibility(true);

        this._reel.texture = GameResources.getTextures("reel")[0];

        this.toggleSideBoards(false);
        this._fsRemaining.text = "";
        this._fsTotalMultiplier.text = "";
        this._fsTotalMultiplier.fontSize = 20; // reset the font size.
        this._isLastSpin = false;
        this._dotRed.visible = false;
        this._winUpToTextItem.visible = !FireInTheHole.api.gameClientConfiguration.showLowProbabilityGfx ? false : true;
        this._dotRedTL.pause();
        this._dotRedTL.kill();

        this._multiplierRestore = 0;

        this._fsThemeOutlineGlow.forEach((outlineGlow: TimelineSprite) => {
            outlineGlow.hide();
            outlineGlow.stopLoop();
        });

        this._killedDwarf.visible = false;
        this._hasKilledDwarf = false;
    }

    /**
     * @description To move the barrier down by required steps.
     * @param steps
     */
    public moveBarrierDown(steps: number): void {
        const moveBy: number = (FITHGameConfig.instance.SYMBOL_HEIGHT * steps),
            fps: number = 30,
            initialDurFrames: number = 12 / fps,
            midDurFrames: number = 1 / fps,
            endDurFrames: number = 2 / fps;

        // #32
        TweenLite.to(this._barrier, initialDurFrames, {
            y: (this._barrier.y) + (moveBy + 2),
            onComplete: () => TweenLite.to(this._barrier, midDurFrames, {
                y: (this._barrier.y) - 6,
                onComplete: () => TweenLite.to(this._barrier, endDurFrames, {y: (this._barrier.y) + 4})
            })
        });

        TweenLite.to(this._barrierMask, initialDurFrames, {
            y: (this._barrierMask.y) - (moveBy + 2),
            onComplete: () => TweenLite.to(this._barrierMask, midDurFrames, {
                y: (this._barrierMask.y) + 6,
                onComplete: () => TweenLite.to(this._barrierMask, endDurFrames, {y: (this._barrierMask.y) - 4})
            })
        });
    }

    public moveBarrierForAS(steps: number): void {
        //reset barrier
        const centre: number = FITHGameConfig.instance.REEL_AREA_WIDTH / 2,
            halfSymHeight: number = (FITHGameConfig.instance.SYMBOL_HEIGHT / 2),
            yOffsetBarrier: number = 7;

        this._barrier.position.y=centre + halfSymHeight + yOffsetBarrier;
        this._barrierMask.position.y=-7;

        //move barrier
        const moveBy: number = (FITHGameConfig.instance.SYMBOL_HEIGHT * steps);
        this._barrier.position.y=this._barrier.y+(moveBy + 2)-6+4;
        this._barrierMask.position.y=this._barrierMask.y-(moveBy + 2)+6-4;
    }

    /**
     * @description To reset the barrier up to it's original position.
     */
    public resetBarrierUp(): void {
        const centre: number = FITHGameConfig.instance.REEL_AREA_WIDTH / 2,
            halfSymHeight: number = (FITHGameConfig.instance.SYMBOL_HEIGHT / 2),
            yOffsetBarrier: number = 7,
            duration: number = 15 / 30;

        if (this._barrier.y !== (centre + halfSymHeight + yOffsetBarrier) && (this._nextMode === FITHGameMode.NORMAL)) {
            // #32
            TweenLite.to(this._barrier, duration, {
                y: centre + halfSymHeight + yOffsetBarrier
            });

            TweenLite.to(this._barrierMask, duration, {
                y: -7
            });

            // #218
            FITHSoundManager.playGridUp();
        }
    }

    // #354
    public activateKilledDwarf(): void {
        this._killedDwarf.alpha = 0;
        this._killedDwarf.visible = true;
        TweenLite.to(this._killedDwarf, 0.3, {alpha: 1});
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners() {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData) {
        super.onResize && super.onResize(resizeData);

        const xOverlapOffset: number = 20,
            yOffset: number = 80,
            killedDwarfXOffset: number = 50,
            killedDwarfYOffset: number = -45;
        let xPos: number = this._leftBoard.width / 2,
            yPos: number = (this._reel.y - this._reel.height / 2),
            xDiff: number = FITHGameConfig.instance.REEL_AREA_WIDTH;

        if (NolimitApplication.isLandscape) {
            xPos = (-(xPos) + xOverlapOffset);
            yPos += yOffset;
            xDiff += (this._rightBoard.width * 3 / 4 - 2);
        } else {
            xDiff -= this._leftBoard.width;
        }

        this._leftBoard.position.set(xPos, yPos);
        this._leftBoardRed.position.set(xPos, yPos);
        this._dotRed.position.set(xPos, yPos);
        this._fsRemaining.position.set(xPos, yPos);
        this._leftBoardAnimation.position.set(xPos, yPos);
        this._rightBoard.position.set(xPos + xDiff, yPos);
        this._fsTotalMultiplier.position.set(xPos + xDiff, yPos + 10);
        this._rightBoardAnimation.position.set(xPos + xDiff, yPos);

        this._killedDwarf.position.set(xPos + xDiff + killedDwarfXOffset, yPos + killedDwarfYOffset);
    }

    /**
     * @inheritDoc
     */
    protected initAnimations() {
        super.initAnimations && super.initAnimations();
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.REEL_OVERLAY.name).addChild(this);

        this.createReel();
        this.createWinMarkupText();
        this.createBarrier();
        this.createTrolley();
        this.createFSThemeOutlineGlow();
        this.createSideBoards();
        this.createSideBoardsTexts();
        this.createSideBoardsUpdateAnimation();

        this.onGameReady();
    }

    private setFSMultiplierPivot(): void {
        let yOffset: number = 0;
        if (this._fsTotalMultiplier.text.length > 5) {
            yOffset = 10;
        } else if (this._fsTotalMultiplier.text.length > 4) {
            yOffset = 7;
        }
        this._fsTotalMultiplier.pivot.set(this._fsTotalMultiplier.width / 2, this._rightBoard.height / 2 - yOffset);
    }

    /**
     * @description To create the reel frame image.
     */
    private createReel(): void {
        const centre: number = FITHGameConfig.instance.REEL_AREA_WIDTH / 2,
            yOffset: number = 50;

        this._reel = new PIXI.Sprite(GameResources.getTextures("reel")[0]);
        this._reel.anchor.set(0.5);
        this._reel.position.set(centre, (centre + yOffset));

        this.addChild(this._reel);
    }

    /**
     * @description To create win markup text on reel.
     */
    private createWinMarkupText(): void {
        const winUpToText: string = SlotGame.api.translations.translate("WIN UP TO") + " 60 000 X";
        this._winUpToTextItem = new PIXI.Text(winUpToText, new PIXI.TextStyle({
            fill: "#000",
            fontFamily: "Arial",
            fontSize: 22,
            fontWeight: FontWeight.BOLD,
            stroke: "#7d4b2e",
            strokeThickness: 4
        }));
        this._winUpToTextItem.position.set(5, 108);
        this._winUpToTextItem.alpha = 0.6;

        // Checking text width should not be greater than 400
        Helper.shrinkTextWidth(winUpToText, this._winUpToTextItem, 400);
        this._winUpToTextItem.visible = !FireInTheHole.api.gameClientConfiguration.showLowProbabilityGfx?false:true;

        this.addChild(this._winUpToTextItem);
    }

    /**
     * @description To create the barrier.
     */
    private createBarrier(): void {
        const centre: number = FITHGameConfig.instance.REEL_AREA_WIDTH / 2,
            yOffset: number = (FITHGameConfig.instance.SYMBOL_HEIGHT / 2) + 7,
            maskShape: PIXI.Graphics = new PIXI.Graphics();

        this._barrier = new PIXI.Sprite(GameResources.getTextures("barrier")[0]);
        this._barrier.anchor.set(0.5, 0);
        this._barrier.position.set(centre, centre + yOffset);

        maskShape.beginFill(0xFFFFFF, 1);
        maskShape.drawRect(-(this._barrier.width / 2), -7, this._barrier.width, this._barrier.height);
        maskShape.endFill();
        this._barrier.addChild(maskShape);
        this._barrier.mask = maskShape;
        this._barrierMask = maskShape;

        this.addChild(this._barrier);
    }

    /**
     * @description To create the side boards.
     */
    private createSideBoards(): void {
        const leftBoard: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("board")[0]),
            rightBoard: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("board")[0]),
            leftBoardRed: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("boardRed")[0]),
            dotRed: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("dotRed")[0]);

        leftBoard.anchor.set(0.5);
        leftBoard.visible = false;

        rightBoard.anchor.set(0.5);
        rightBoard.visible = false;

        leftBoardRed.anchor.set(0.5);
        leftBoardRed.visible = false;
        leftBoardRed.blendMode = PIXI.BLEND_MODES.SCREEN;

        dotRed.anchor.set(0.5);
        dotRed.visible = false;
        dotRed.blendMode = PIXI.BLEND_MODES.SCREEN;

        this._leftBoard = leftBoard;
        this._rightBoard = rightBoard;
        this._leftBoardRed = leftBoardRed;
        this._dotRed = dotRed;

        this._dotRedTL = new TimelineMax({
            paused: true,
            yoyo: true,
            repeat: -1
        });
        this._dotRedTL.add(TweenLite.to(this._dotRed, 15 / 30, {alpha: 1}));
        this._dotRedTL.add(TweenLite.to(this._dotRed, 15 / 30, {alpha: 0}));

        this._killedDwarf = new PIXI.Sprite(GameResources.getTextures("dead")[0]);
        this._killedDwarf.visible = false;
        this._killedDwarf.anchor.set(0.5);
        this._killedDwarf.scale.set(0.35);

        this.addChild(leftBoard, rightBoard, leftBoardRed, dotRed, this._killedDwarf);
    }

    /**
     * @description To create the side boards' text.
     */
    private createSideBoardsTexts(): void {
        this._fsRemaining = new BitmapCountUp({
            style: {
                font: {
                    name: "NumbersWays",
                    size: 34
                },
                tint: parseInt(("#FFFFFF").replace(/^#/, ''), 16)
            },
            numberFormat: {
                precision: 0
            }
        });

        this._fsTotalMultiplier = new BitmapCountUp({
            style: {
                font: {
                    name: "NumbersWinCountUp",
                    size: 20
                },
                tint: parseInt(("#FFFFFF").replace(/^#/, ''), 16)
            }
        });

        this._fsRemaining.maxTextWidth = this._leftBoard.width;
        this._fsRemaining.visible = false;
        this._fsRemaining.text = "";

        this._fsTotalMultiplier.maxTextWidth = this._rightBoard.width - 15;
        this._fsTotalMultiplier.visible = false;
        this._fsTotalMultiplier.text = "";

        this.addChild(this._fsRemaining, this._fsTotalMultiplier);
    }

    /**
     * @description To create the side boards' update animation.
     */
    private createSideBoardsUpdateAnimation(): void {
        this._leftBoardAnimation = new TimelineSprite(GameResources.getTextures("reset"));
        this._leftBoardAnimation.anchor.set(0.5);
        this._leftBoardAnimation.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._leftBoardAnimation.hide();

        this._rightBoardAnimation = new TimelineSprite(GameResources.getTextures("refresh"));
        this._rightBoardAnimation.anchor.set(0.5);
        this._rightBoardAnimation.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._rightBoardAnimation.hide();

        this.addChild(this._leftBoardAnimation, this._rightBoardAnimation);
    }

    /**
     * @description To create the trolley.
     */
    private createTrolley(): void {
        const x: number = 570,
            y: number = 925;

        this._trolley = new PIXI.Sprite(ImgLoader.getImgTexture("bgTrolley"));
        this._trolley.anchor.set(0.5);
        this._trolley.position.set(x, y);

        this.addChild(this._trolley);
    }

    /**
     * @description To create the outline glow in bonus game.
     */
    private createFSThemeOutlineGlow(): void {
        let item: TimelineSprite;
        const numItems: number = 2,
            gameConfig: FITHGameConfig = FITHGameConfig.instance;

        this._fsThemeOutlineGlow = []; // items are in left, right, top, bottom order.

        //adding left and right items
        for (let i: number = 0; i < numItems; i++) {
            item = new TimelineSprite(GameResources.getTextures("fsThemeOutlineGlow"));
            item.hide();
            item.blendMode = PIXI.BLEND_MODES.SCREEN;
            if (i === 0) {
                item.rotation = (-90 * Math.PI) / 180;
                item.position.set(-27, 390);
            } else {
                item.rotation = (90 * Math.PI) / 180;
                item.position.set(750, 390);
            }
            item.scale.x *= 0.82;
            item.anchor.set(0.5);
            this._fsThemeOutlineGlow.push(item);
            this.addChild(item);
        }
        //adding top and bottom items
        for (let i: number = 0; i < numItems; i++) {
            item = new TimelineSprite(GameResources.getTextures("fsThemeOutlineGlow"));
            item.hide();
            item.blendMode = PIXI.BLEND_MODES.SCREEN;
            if (i === 0) {
                item.position.set(362, 74);
            } else {
                item.scale.y *= -1;
                item.position.set(362, 715);
            }
            item.anchor.set(0.5);
            this._fsThemeOutlineGlow.push(item);
            this.addChild(item);
        }
    }

    /**
     * @description Init data handler.
     * @param data
     */
    private onInitDataParsed(data: FITHParsedInitData): void {
        this._nextMode = data.nextMode;
        if (data.isRestoreState) {
            this._activeRows = data.activeRows;
            this._fsRemainingValue = (data.freespinsLeft - 1);
            this._multiplierRestore = data.bonusWinTimesBet;

            // #354
            let totalOnBoard: number = 0;
            for (let i: number = 0; i < data.bonusValues.length; i++) {
                for (let j: number = 0; j < data.bonusValues[i].length; j++) {
                    totalOnBoard += data.bonusValues[i][j];
                }
            }
            if (totalOnBoard < data.bonusWinTimesBet) {
                this._hasKilledDwarf = true;
            }

            this.onGameReady();
        }

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    /**
     * @description Game data handler.
     * @param data
     */
    private onGameDataParsed(data: FITHParsedGameData): void {
        this._nextMode = data.nextMode;
        this._fsRemainingValue = (data.freespinsLeft - 1);
    }

    /**
     * @description When the game is ready.
     */
    private onGameReady(): void {
        // make sure that the required data is there and also the item has been created.
        if (this._activeRows && this._barrier) {
            const defaultActiveRows: number = 3;
            this.moveBarrierDown(this._activeRows - defaultActiveRows);

            // #205
            this.updateTotalMultiplierBoardText(this._multiplierRestore);
            this.updateSpinsLeftBoardText();
        }

        // #354
        if (this._hasKilledDwarf && this._killedDwarf) {
            this._killedDwarf.visible = true;
        }
    }

    /**
     * @description Toggle barrier's visibility.
     * @param visible
     */
    private toggleBarrierVisibility(visible: boolean): void {
        this._barrier.visible = visible;
    }

    /**
     * @description Toggle side boards's visibility.
     * @param visible
     */
    private toggleSideBoards(visible: boolean): void {
        this._leftBoard.visible = visible;
        this._rightBoard.visible = visible;

        this._fsRemaining.visible = visible;
        this._fsTotalMultiplier.visible = visible;
    }
}